import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@/lib/utils';
import { billsApi, billsQueryKeys } from '../api';
import type { BillFilters, CreateBillDto, UpdateBillDto } from '../types';

// Hook for fetching bills list
export function useBillsList(filters?: BillFilters) {
  return useQuery({
    queryKey: billsQueryKeys.list(filters),
    queryFn: () => billsApi.getAll(filters),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Billed / paid roll-up for the whole filtered set (one bucket per currency),
 * backing the summary strip above the bills table.
 *
 * Paging and sorting are stripped from the key so the numbers don't refetch —
 * or flicker — when the user pages through the table.
 *
 * `retry: false`: the aggregate is a decoration on a page that works without
 * it, so a failure should degrade to "—" immediately rather than retry-storm.
 */
export function useBillsSummary(filters?: BillFilters, enabled = true) {
  const { page, limit, offset, orderBy, order, ...rest } =
    (filters ?? {}) as BillFilters & { offset?: number };
  void page;
  void limit;
  void offset;
  void orderBy;
  void order;

  return useQuery({
    queryKey: billsQueryKeys.summary(rest as BillFilters),
    queryFn: () => billsApi.getSummary(rest as BillFilters),
    // Gated by the caller: without a contract there is no single currency to
    // total, so we don't spend a request on a figure the UI won't show.
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

// Hook for fetching single bill
export function useBill(id: number | string | undefined) {
  return useQuery({
    queryKey: billsQueryKeys.detail(id ?? ''),
    queryFn: () => billsApi.getById(id!),
    enabled: !!id,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });
}

// Hook for bill mutations
export function useBillMutations() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (data: CreateBillDto) => billsApi.create(data),
    onSuccess: () => {
      toast.success(t('billCreatedSuccess'));
      void queryClient.invalidateQueries({ queryKey: billsQueryKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorCreatingBill')));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number | string; data: UpdateBillDto }) =>
      billsApi.update({ id, data }),
    onSuccess: (_, { id }) => {
      toast.success(t('billUpdatedSuccess'));
      void queryClient.invalidateQueries({ queryKey: billsQueryKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: billsQueryKeys.detail(id) });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorUpdatingBill')));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number | string) => billsApi.delete(id),
    onSuccess: () => {
      toast.success(t('billDeletedSuccess'));
      void queryClient.invalidateQueries({ queryKey: billsQueryKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorDeletingBill')));
    },
  });

  return {
    createMutation,
    updateMutation,
    deleteMutation,
  };
}

// Hook for bill status changes
export function useBillStatusMutations() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const acceptMutation = useMutation({
    mutationFn: (id: number | string) => billsApi.accept(id),
    onSuccess: (_, id) => {
      toast.success(t('billAcceptedSuccess'));
      void queryClient.invalidateQueries({ queryKey: billsQueryKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: billsQueryKeys.detail(id) });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorAcceptingBill')));
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (id: number | string) => billsApi.archive(id),
    onSuccess: (_, id) => {
      toast.success(t('billArchivedSuccess'));
      void queryClient.invalidateQueries({ queryKey: billsQueryKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: billsQueryKeys.detail(id) });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorArchivingBill')));
    },
  });

  return {
    acceptMutation,
    archiveMutation,
  };
}

// Hook for downloading bill
export function useBillDownload() {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, format }: { id: number | string; format: string }) =>
      billsApi.download(id, format),
    onSuccess: (blob, { id, format }) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bill-${id}.${format.toLowerCase()}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(t('billDownloadedSuccess'));
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorDownloadingBill')));
    },
  });
}
